// Service API pour les Certifications (appels sécurisés côté serveur)

export interface Certification {
    id: string;
    title: string;
    issuer: string;
    type: 'certification' | 'badge';
    date: number; // timestamp
    imageUrl: string;
    url: string;
    description: string;
    credentialId: string;
    createdAt?: number;
}

const API_BASE = '/api/certifications';

// Récupérer le token d'authentification
function getAuthToken(): string | null {
    return localStorage.getItem('admin_token');
}

// Headers par défaut
function getHeaders(): HeadersInit {
    const token = getAuthToken();
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
}

// Liste toutes les certifications
export async function getCertifications(): Promise<Certification[]> {
    const response = await fetch(API_BASE, {
        headers: getHeaders(), // Even for GET we can send it, though it's public
    });

    if (!response.ok) {
        throw new Error('Failed to fetch certifications');
    }

    const data = await response.json();
    return data.certifications;
}

// Créer une certification
export async function createCertification(cert: Omit<Certification, 'id' | 'createdAt'>): Promise<Certification> {
    const response = await fetch(API_BASE, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(cert),
    });

    if (!response.ok) {
        if (response.status === 401) {
            throw new Error('Unauthorized');
        }
        throw new Error('Failed to create certification');
    }

    return response.json();
}

// Importer depuis Credly
export async function importCredlyBadge(url: string): Promise<Partial<Certification>> {
    const response = await fetch(`${API_BASE}/import-credly`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ url }),
    });

    if (!response.ok) {
        if (response.status === 401) {
            throw new Error('Unauthorized');
        }
        throw new Error('Failed to import from Credly');
    }

    return response.json();
}

// Mettre à jour une certification
export async function updateCertification(id: string, updates: Partial<Certification>): Promise<Certification> {
    const response = await fetch(`${API_BASE}/${id}`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify(updates),
    });

    if (!response.ok) {
        if (response.status === 401) {
            throw new Error('Unauthorized');
        }
        throw new Error('Failed to update certification');
    }

    return response.json();
}

// Supprimer une certification
export async function deleteCertification(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/${id}`, {
        method: 'DELETE',
        headers: getHeaders(),
    });

    if (!response.ok) {
        if (response.status === 401) {
            throw new Error('Unauthorized');
        }
        throw new Error('Failed to delete certification');
    }
}
